/**
 * Notifications API client.
 *
 * Mirrors `backend/src/hub/routes/notifications.py`. The bell +
 * the (future) notifications page consume from here.
 *
 * State semantics:
 *   - ``read_at == null``       → unread (counts toward the badge)
 *   - ``read_at != null && dismissed_at == null`` → read but kept
 *   - ``dismissed_at != null``  → hidden by default in the bell
 */
import { api } from "./api";


export type NotificationState = "all" | "unread" | "read" | "dismissed";

export type NotificationSeverity = "info" | "success" | "warning" | "error";


export interface NotificationItem {
  id: string;
  kind: string;
  severity: NotificationSeverity;
  title: string;
  subtitle: string | null;
  related_workflow_id: string | null;
  related_execution_id: string | null;
  payload: Record<string, unknown> | null;
  /** Number of times this notification has fired (coalesced). 1 = single event. */
  occurrence_count: number;
  /** Most recent occurrence — used by the UI to show "last seen 12s ago". */
  last_occurred_at: string | null;
  created_at: string;
  read_at: string | null;
  dismissed_at: string | null;
}


export interface NotificationsResponse {
  items: NotificationItem[];
  /** Total unread across ALL pages — drives the badge. */
  unread_count: number;
  total: number;
}


export const notificationsApi = {
  list: (params?: { state?: NotificationState; limit?: number }) =>
    api.get<NotificationsResponse>("/notifications", { query: params }),

  count: () => api.get<{ unread: number }>("/notifications/count"),

  markRead: (id: string) =>
    api.post<NotificationItem>(`/notifications/${id}/read`),

  dismiss: (id: string) =>
    api.post<NotificationItem>(`/notifications/${id}/dismiss`),

  markAllRead: () =>
    api.post<{ marked: number }>("/notifications/mark-all-read"),
};
